// Client-side mirror of backend "Report Management Service/utils/leadValidation.js".
//
// The server is authoritative — it also checks that the email's domain can receive mail,
// which a browser cannot — but running the same rules here lets the unlock form flag a
// problem under the field as the visitor leaves it, instead of after a round trip. There is
// no shared build between the two, so change both together.

const MAX_NAME = 50;
const MAX_COMPANY = 100;

const NAME_CHARS = /^[\p{L}\p{M}][\p{L}\p{M}' .’-]*$/u;
const LATIN_LETTER = /[a-z]/i;
const LATIN_VOWEL = /[aeiouyàáâãäåæèéêëìíîïòóôõöøùúûüýÿœ]/i;

const tidy = (v) => String(v ?? '').trim().replace(/\s+/g, ' ');

export function nameError(raw, label) {
  const v = tidy(raw);
  if (!v) return `Enter your ${label}.`;
  if (v.length > MAX_NAME) return `${label[0].toUpperCase()}${label.slice(1)} must be ${MAX_NAME} characters or fewer.`;
  if (!NAME_CHARS.test(v)) return `Use letters only in your ${label} — no numbers or symbols.`;
  for (const word of v.split(/[ .'’-]+/)) {
    if (word.length >= 4 && LATIN_LETTER.test(word) && !LATIN_VOWEL.test(word)) {
      return `That doesn't look like a real ${label}.`;
    }
  }
  if (/(.)\1{4,}/iu.test(v)) return `That doesn't look like a real ${label}.`;
  return null;
}

const LOCAL_PART = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const DOMAIN_LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i;
const TLD = /^(xn--[a-z0-9-]{2,59}|[a-z]{2,63})$/i;

export function emailSyntaxError(raw) {
  const v = String(raw ?? '').trim();
  if (!v) return 'Enter your email address.';
  if (v.length > 254) return 'That email address is too long.';
  const at = v.lastIndexOf('@');
  if (at < 1 || at === v.length - 1) return 'Enter a valid email address, like name@company.com.';
  const local = v.slice(0, at);
  const domain = v.slice(at + 1);
  if (local.length > 64 || !LOCAL_PART.test(local)) return 'Enter a valid email address, like name@company.com.';
  const labels = domain.split('.');
  if (labels.length < 2 || !labels.every((l) => DOMAIN_LABEL.test(l)) || !TLD.test(labels[labels.length - 1])) {
    return 'Enter a valid email address, like name@company.com.';
  }
  return null;
}

const PHONE_CHARS = /^\+?[\d\s().-]+$/;

function looksMashed(digits) {
  if (/^(\d)\1+$/.test(digits)) return true;
  if (/^(\d{2})\1{3,}\d?$/.test(digits)) return true;
  if (/^(\d{3})\1{3,}\d{0,2}$/.test(digits)) return true;
  return '01234567890123456789'.includes(digits) || '98765432109876543210'.includes(digits);
}

function nanpError(ten) {
  const area = ten.slice(0, 3);
  const exchange = ten.slice(3, 6);
  if (!/^[2-9]\d\d$/.test(area) || /^[2-9]11$/.test(area)) return 'That area code isn\'t valid.';
  if (!/^[2-9]\d\d$/.test(exchange) || /^[2-9]11$/.test(exchange)) return 'Enter a valid phone number.';
  if (exchange === '555' && /^01\d\d$/.test(ten.slice(6))) return 'Enter a valid phone number.';
  return null;
}

export function phoneError(raw) {
  const v = String(raw ?? '').trim();
  if (!v) return 'Enter your phone number.';
  if (!PHONE_CHARS.test(v)) return 'Use digits only — spaces, dashes and brackets are fine.';
  let digits = v.replace(/\D/g, '');
  if (v.startsWith('+') && !digits.startsWith('1')) {
    if (digits.length < 8 || digits.length > 15 || digits.startsWith('0')) {
      return 'Enter a valid international number, like +44 20 7946 0958.';
    }
    return looksMashed(digits) ? 'Enter a valid phone number.' : null;
  }
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) {
    return 'Enter a 10-digit number, or include your country code (e.g. +44 20 7946 0958).';
  }
  if (looksMashed(digits)) return 'Enter a valid phone number.';
  return nanpError(digits);
}

export function companyError(raw, required = true) {
  const v = tidy(raw);
  if (!v) return required ? 'Enter your company name.' : null;
  if (v.length < 2) return 'Enter your full company name.';
  if (v.length > MAX_COMPANY) return `Company name must be ${MAX_COMPANY} characters or fewer.`;
  if (!/[\p{L}\p{N}]/u.test(v)) return 'Enter a valid company name.';
  if (/(.)\1{4,}/u.test(v)) return 'Enter a valid company name.';
  return null;
}

/** Error message for one field of the unlock form, or null when it is fine. */
export function leadFieldError(field, value) {
  switch (field) {
    case 'firstName': return nameError(value, 'first name');
    case 'lastName': return nameError(value, 'last name');
    case 'email': return emailSyntaxError(value);
    case 'phone': return phoneError(value);
    case 'company': return companyError(value, false);
    default: return null;
  }
}

export const LEAD_FIELDS = ['firstName', 'lastName', 'email', 'phone', 'company'];

/** Every field's error, keyed by field; empty when the form is valid. */
export function leadFormErrors(form) {
  const errors = {};
  LEAD_FIELDS.forEach((f) => {
    const msg = leadFieldError(f, form[f]);
    if (msg) errors[f] = msg;
  });
  return errors;
}
